// =============================================================================
// MeuDriver — Landing Testimonials + Instructor CTA (Sections 6 + 7)
// =============================================================================

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Car, Star } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { PLATFORM_FEE_PERCENT, PAYOUT_WINDOW_HOURS } from '@/lib/constants'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay } }),
}
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }
const viewportOnce = { once: true, margin: '-80px' }

const GRADIENTS = ['from-brand-500 to-brand-700', 'from-teal-500 to-teal-700', 'from-orange-400 to-orange-600']

/** Menos que isso não vira vitrine — evita a página mostrar uma avaliação solta (ou de teste). */
const MIN_REVIEWS_TO_SHOW = 3

interface LandingReview {
  id: string
  rating: number
  comment: string
  anonymous: boolean | null
  student: { full_name: string | null } | null
}

/** "Juliana Fernandes" → "Juliana F." (privacidade do aluno na página inicial). */
function shortName(fullName: string | null | undefined): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'Aluno MeuDriver'
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0]
}

function initialsOf(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()
}

/** Avaliações reais (nota 4+ com comentário), mais recentes primeiro. */
function useLandingReviews() {
  return useQuery<LandingReview[]>({
    queryKey: ['landing-reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('id, rating, comment, anonymous, student:profiles!reviews_student_id_fkey(full_name)')
        .gte('rating', 4)
        .not('comment', 'is', null)
        .order('created_at', { ascending: false })
        .limit(12)
      if (error) throw error
      return ((data ?? []) as unknown as LandingReview[])
        .filter((r) => (r.comment ?? '').trim().length >= 30)
        .slice(0, 3)
    },
    staleTime: 1000 * 60 * 30,
  })
}

export function LandingTestimonials() {
  const { data: reviews } = useLandingReviews()

  // Sem avaliações reais suficientes, a seção não aparece (nada de depoimento inventado).
  if (!reviews || reviews.length < MIN_REVIEWS_TO_SHOW) return null

  return (
    <section className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }} className="text-center mb-12">
          <Badge variant="success" className="mb-3 text-sm px-3 py-1">Avaliações</Badge>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl tracking-tight">O que nossos alunos dizem</h2>
          <p className="mt-3 text-gray-500">Avaliações deixadas por alunos depois das aulas na MeuDriver.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid gap-5 sm:grid-cols-3">
          {reviews.map((r, i) => {
            const name = r.anonymous ? 'Aluno MeuDriver' : shortName(r.student?.full_name)
            return (
              <motion.div key={r.id} variants={fadeUp} custom={i * 0.1}>
                <div className="relative h-full rounded-2xl border border-gray-100 bg-white shadow-soft hover:shadow-card transition-shadow duration-300 p-6 flex flex-col gap-4 overflow-hidden">
                  <span className="absolute top-3 right-4 text-8xl font-black leading-none text-gray-50 select-none pointer-events-none" aria-hidden>"</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: Math.round(r.rating) }).map((_, si) => (
                      <Star key={si} className="h-4 w-4 fill-orange-400 text-orange-400" />
                    ))}
                  </div>
                  <blockquote className="text-sm text-gray-600 leading-relaxed flex-1 relative">{r.comment}</blockquote>
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${GRADIENTS[i % GRADIENTS.length]} flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm`}>
                      {initialsOf(name)}
                    </div>
                    <p className="text-sm font-semibold text-gray-800">{name}</p>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

export function LandingCTA() {
  return (
    <section className="bg-slate-950 py-20 sm:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-700/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-teal-900/30 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      </div>

      <div className="relative mx-auto max-w-3xl px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.55 }} className="flex flex-col items-center gap-6">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-teal-500 shadow-xl">
            <Car className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl tracking-tight">Você é instrutor de direção?</h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed max-w-xl mx-auto">
              Cadastre-se na MeuDriver e amplie sua base de alunos. Receba pagamentos com segurança e gerencie sua agenda online.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button asChild size="lg" variant="orange" className="px-8 text-base shadow-orange/20 font-semibold">
              <Link to="/seja-instrutor">
                Começar agora
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" className="px-8 text-base bg-white/10 text-white border border-white/15 hover:bg-white/20 hover:border-white/25 transition-all">
              <Link to="/instrutores">Conhecer a plataforma</Link>
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 pt-5 border-t border-white/8 w-full">
            {[
              { label: 'Você recebe do seu preço', value: '100%' },
              { label: 'Pagamento rápido', value: `${PAYOUT_WINDOW_HOURS}h` },
              { label: 'Suporte dedicado', value: '7 dias' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-2xl font-extrabold text-teal-400">{item.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
